import { actAs } from '@w/crdt'
import { consumeAppliedPageKeys } from '../db/applied-page-keys'
import { startPageSearchRefresh } from '../db/page-search-projection'
import { classify, splitStatements, createCustomDriver, type CustomDriver } from '@w/db-custom'
import { flashcardsLocalTables, replicatedTables, workspaceTables } from '../db/workspace-tables'
import { persistRows, type PendingRow } from '@w/db-replication/pending-rows'
import { writeWorkspace } from '../db/workspace-write'
import { createWriteGate } from '../db/page-commit-buffer'
import { commitPageEdits, type CommitPageEditInput } from '../db/commit-page-edit'
import { listPageUnloads } from '../db/page-unload-journal'
import type { TransportStatusDriver } from './replica-transport-status'
import { isDeletedWorkspaceReset, resetSpaceReplicaWhenIdle } from './space-generation-reset'
import { readReplicaState, ReplicaTransportError } from '@w/db-turso-browser-adapter/worker'
import type { createPageTranslationsBackground } from '../translation/page-translations-background'
import type { TursoReplicaState, TursoWorkerDatabaseDriver, TursoWorkerOwnership } from '@w/db-turso-browser-adapter/worker'
import { cdbPreCommit, createDeadLetterService, ReplicationInvariantError, type DeadLetterService } from '@w/db-replication'
import { QueryClient } from '@w/react-query'
import { RpcError } from '@w/rpc'
import type { Tel } from '@w/tel'
import type { DatabaseDriver } from '@w/db'
import { type ClientDb } from '../db/db'
import { createDatabaseRenames, type DatabaseRenames } from '../databases/database-renames'
import { openWorkspaceReplication, type SyncHalt } from '../db/workspace-replication'
import { isReplicationOffline, isReplicationOutcome, isReplicationTransient, isWorkspaceAccessRejection } from '../db/workspace-replication-errors'
import type { PageEmbeddingsBrowserClient } from '../search/page-embeddings-browser.types'
import { exportPendingWork, importPendingWork, pendingStore, persistLocalWork, persistPendingWork, reexpressPendingWork } from '../db/reexpress-pending'
import { holdResetImport, readResetImports, releaseResetImport } from './reset-import-hold'
import { disposeAll, expectedRenderError, safe, unknownErrorToError, ulid } from '@w/utils'

/** `personalRootId`: the caller's personal root page in the workspace, from its directory entry (never derived from a user id). */
export type Space = { id: string; workspaceId: string; kind: 'private' | 'shared'; ownerUserId: string; personalRootId: string; name: string }
/** Commits each page burst a closed page left in the unload journal of (userId, workspaceId), as that user; a
 * committed entry leaves the journal, a refused one stays for the next open, a malformed one is reported and dropped.
 * Run when a workspace file opens, and on an attach source before it is staged (flashcards-local-first.spec.md
 * Attach step 2). */
export async function replayPageUnloads(input: { storage: Storage; userId: string; workspaceId: string; tel: Tel; commit: (pageId: string, frame: Uint8Array) => Promise<unknown> }) {
  for (const stored of listPageUnloads(input.storage, input.userId, input.workspaceId)) {
    let entry
    try { entry = stored.read() }
    catch (error) {
      // A malformed entry can never commit: report and drop it, then go on with the rest.
      input.tel.error(error, { operation: 'page.unloadJournal', workspaceId: input.workspaceId })
      input.storage.removeItem(stored.key)
      continue
    }
    const { pageId, frame } = entry
    try {
      // A frame the page already holds commits as no edit (authored-source-batch `unchanged`).
      await input.commit(pageId, frame)
      input.storage.removeItem(stored.key)
    } catch (error) {
      // Kept for the next open: a refused burst is reported, never dropped.
      input.tel.error(error, { operation: 'page.unloadJournal', workspaceId: input.workspaceId, pageId })
    }
  }
}

export type SpaceFile = {
  /** A fresh file's initial replay already fulfilled its opening sync demand. */
  syncedOnOpen?: boolean
  db: ClientDb; path: string; replicaIdentity: Uint8Array; ownership?: TursoWorkerOwnership
  translations?: ReturnType<typeof createPageTranslationsBackground>; embeddings?: PageEmbeddingsBrowserClient
  close(): Promise<void>; purge(): Promise<void>; createFreshReplica?(): void
  resetReplica?(): Promise<void>
  /** Closes the file and marks a fresh one; the retired file is deleted once a fresh replica holds its saved work. */
  retireReplica?(): Promise<void>
}
export type SpaceResource = SpaceFile & { customDb: CustomDriver; deadLetters: DeadLetterService; databaseRenames: DatabaseRenames; space: Space; userId: string; replicaId: string; connectionId: string; queryClient: QueryClient; replication: Awaited<ReturnType<typeof openWorkspaceReplication>> }
export type SpacesView = { resources: SpaceResource[]; unavailable: { space: Space; error: unknown }[]; accessLost: boolean }
export type WorkspaceSyncStatus = { offline: boolean; lastSyncedAt: number | null; error: unknown; halted?: SyncHalt | null; pending?: boolean }
type WorkspaceFile = SpaceFile & { customDb: CustomDriver; deadLetters: DeadLetterService; databaseRenames: DatabaseRenames; replicaId: string; connectionId: string; queryClient: QueryClient; replication: SpaceResource['replication'] }

/**
 * Typed refusal of a read issued after a dropped workspace's readers were closed; routes render it as "unavailable".
 * Expected: replace() refuses reads before the withdrawal unmounts the page, so a boundary may catch it without logging.
 */
export class WorkspaceClosedError extends Error {
  readonly [expectedRenderError] = true
  constructor() { super('Workspace closed'); this.name = 'WorkspaceClosedError' }
}

/**
 * Gates the exposed resource's driver; the file owner retains its original driver for reset and reopen:
 * `drained` resolves when no read is in flight; after `refuse` later reads and subscriptions reject.
 * Releasing a subscription is a no-op once the file closes: its close released every subscription, and a
 * reader unmounting later (React cleanup) must not reach the closed engine.
 */
const readerDriverSources = new WeakMap<DatabaseDriver, DatabaseDriver>()

function createReaderGate(retainedDriver: DatabaseDriver) {
  // Reopening a retained database creates a new reader owner. Wrap the engine,
  // rather than inheriting the previous owner's closed subscription gate.
  const driver = readerDriverSources.get(retainedDriver) ?? retainedDriver
  const inflight = new Set<Promise<unknown>>()
  let refusing = false
  let closed = false
  const exposed = { ...driver }
  readerDriverSources.set(exposed, driver)
  const methods = exposed as unknown as Record<string, ((...args: unknown[]) => Promise<unknown>) | undefined>
  const track = (original: (...args: unknown[]) => Promise<unknown>, owner: unknown = driver) => (...args: unknown[]) => {
    if (refusing) return Promise.reject(new WorkspaceClosedError())
    const task = original.apply(owner, args)
    inflight.add(task)
    const release = () => { inflight.delete(task) }
    task.then(release, release)
    return task
  }
  const refuse = (original: (...args: unknown[]) => Promise<unknown>) => (...args: unknown[]) => refusing || closed ? Promise.reject(new WorkspaceClosedError()) : original.apply(driver, args)
  for (const key of ['query', 'exec', 'copyFrom', 'transaction', 'sourceTransaction']) { const original = methods[key]; if (original) methods[key] = track(original) }
  // Contained readers await native guards between SQL reads. Those commands
  // belong to the same reader lifetime and must drain before session close.
  const failures = (driver as TursoWorkerDatabaseDriver).failures
  if (failures) {
    const guarded = { ...failures }
    const commands = guarded as unknown as typeof methods
    for (const key of ['version', 'record', 'resolve', 'list', 'detail', 'retry', 'discard', 'sourceTransaction']) {
      const original = commands[key]
      if (original) commands[key] = track(original, failures)
    }
    Object.assign(exposed, { failures: guarded })
  }
  for (const key of ['subscribe', 'subscribeIds']) { const original = methods[key]; if (original) methods[key] = refuse(original) }
  // Reactive drivers (Turso) add these beyond DatabaseDriver.
  type Releases = { subscribe?: (sql: string, onInvalidate: () => void) => Promise<() => void>; unsubscribeId?: (id: number) => unknown }
  const releases = exposed as Releases
  const subscribe = releases.subscribe
  if (subscribe) releases.subscribe = async (sql, onInvalidate) => {
    const release = await subscribe(sql, onInvalidate)
    return () => { if (!closed) release() }
  }
  const unsubscribeId = (driver as Releases).unsubscribeId
  if (unsubscribeId) releases.unsubscribeId = id => { if (!closed) unsubscribeId.call(driver, id) }
  return {
    driver: exposed,
    original: driver,
    refuse() { refusing = true },
    close() { closed = true },
    async drained() { while (inflight.size > 0) await Promise.allSettled(inflight) },
  }
}

/** One physical database, query client and native replica per workspace. Spaces only qualify reads. */
export function createSpacesResources(options: {
  userId: string; tel: Tel; directoryClient: QueryClient; storage: Storage
  /** Anonymous work stays on this device until attachment to an account. */
  remote?: boolean
  writes?: ReturnType<typeof createWriteGate>
  /** Media lifecycle for page edits committed here (the unload journal); a page edit removing media requires it. */
  bucket?: () => CommitPageEditInput['bucket']
  list(workspaceId: string, tel: Tel, signal: AbortSignal): Promise<Space[]>
  open(space: Space, bootTel: Tel, signal: AbortSignal): Promise<SpaceFile>
  /** Resolves only when no reader of the space's page state holds it. */
  closeReaders?(spaceId: string): void | Promise<void>
  /** Live collaboration follows the workspace's file: opened with it, closed with it, poked after a round that pushed. */
  realtime?: { opened(workspaceId: string): void; closed(workspaceId: string): void; pushed(workspaceId: string): void }
}) {
  const writes = options.writes ?? createWriteGate()
  const appTables = [...new Set([...Object.keys(workspaceTables), ...flashcardsLocalTables])]
  const directories = new Map<string, Space[]>()
  const files = new Map<string, WorkspaceFile>()
  const opening = new Map<string, Promise<boolean>>()
  const acquiring = new Set<Promise<unknown>>()
  const triggering = new Map<string, Set<Promise<void>>>()
  const failures = new Map<string, unknown>()
  const syncStatuses = new Map<string, WorkspaceSyncStatus>()
  const lifetime = new AbortController()
  const replacing = new Map<string, Promise<void>>()
  const readerGates = new WeakMap<WorkspaceFile, ReturnType<typeof createReaderGate>>()
  let closing: Promise<void> | undefined
  function snapshot(workspaceId: string): SpacesView {
    const spaces = lifetime.signal.aborted ? [] : directories.get(workspaceId) ?? []
    const file = lifetime.signal.aborted || replacing.has(workspaceId) ? undefined : files.get(workspaceId)
    return { resources: file ? spaces.map(space => ({ ...file, space, userId: options.userId })) : [],
      unavailable: file ? [] : spaces.map(space => ({ space, error: failures.get(workspaceId) ?? new Error('First download needed') })),
      accessLost: false }
  }
  function gateOf(file: WorkspaceFile) {
    let gate = readerGates.get(file)
    if (!gate) { gate = createReaderGate(safe(file.db.driver)); file.db.setDriver(gate.driver); readerGates.set(file, gate) }
    return gate
  }
  const outboxSql = 'SELECT EXISTS(SELECT 1 FROM _turso_replication_outbox) AS pending'
  // One live observation per open driver: peer notification and sync joins consume the same read.
  const outboxes = new WeakMap<DatabaseDriver, Promise<{ read(): Promise<number | undefined>; invalidate(): void; close(): void }>>()
  function observeOutbox(driver: DatabaseDriver) {
    let observation = outboxes.get(driver)
    if (!observation) {
      observation = (async () => {
        let reading: Promise<number | undefined> | undefined
        const release = await (driver as TursoWorkerDatabaseDriver).subscribe(outboxSql, () => { reading = undefined })
        return { close: release, invalidate() { reading = undefined }, read() {
          return reading ??= driver.query<{ pending: number }>(outboxSql, undefined, { tel: options.tel })
            .then(rows => rows[0]?.pending).catch((error: unknown) => { reading = undefined; throw error })
        } }
      })()
      outboxes.set(driver, observation)
    }
    return observation
  }
  /** Workspaces with a local commit whose push no poke announced yet. */
  const unpoked = new Set<string>()
  /**
   * After an exchange that left the outbox empty, a local commit since the last poke has reached the server: poke the
   * workspace's peers so they pull (page-collaboration.spec.md, On the page). An exchange that started before the
   * commit leaves it in the outbox, so the poke waits for the round that pushes it; a burst of commits pokes once.
   */
  async function pokeAfterPush(workspaceId: string, driver: DatabaseDriver) {
    if (!options.realtime || !unpoked.has(workspaceId)) return
    const outbox = await observeOutbox(driver)
    if (await outbox.read().catch(() => null) !== 0) return
    unpoked.delete(workspaceId)
    options.realtime.pushed(workspaceId)
  }
  const resetMarker = Symbol('reset required')
  /** A failed inbound dead letter whose reason is the engine's RESET_REQUIRED (fork sync/engine/src/replication/failures.rs:774). */
  async function resetRequired(file: WorkspaceFile) {
    const sql = `SELECT EXISTS(SELECT 1 FROM _turso_dead_letters WHERE category='inbound' AND status='failed' AND reasons LIKE '%RESET_REQUIRED%') AS required`
    const rows = await gateOf(file).original.query<{ required: number }>(sql, undefined, { tel: options.tel }).catch(() => null)
    return rows?.[0]?.required === 1
  }
  function publishPermissions() {
    // The workspace directory is server-owned: only membership commands invalidate it, never a pull.
    // Refetching active page queries runs on the workspace db queue; sync/close must not wait on it.
    void options.directoryClient.invalidateQueries({ queryKey: ['page'] }).catch(() => {})
  }
  function queryOptions(workspaceId: string) {
    return { queryKey: ['space-resources', options.userId, workspaceId], queryFn: () => Promise.resolve(snapshot(workspaceId)), staleTime: Infinity }
  }
  function publish(workspaceId: string) { return options.directoryClient.invalidateQueries({ queryKey: queryOptions(workspaceId).queryKey }) }
  function syncStatusOptions(workspaceId: string) {
    return { queryKey: ['workspace-sync', options.userId, workspaceId], staleTime: Infinity,
      queryFn: () => Promise.resolve(syncStatuses.get(workspaceId) ?? { offline: false, lastSyncedAt: lastSync(workspaceId), error: null }) }
  }
  function lastSync(workspaceId: string) {
    const value = options.storage.getItem(JSON.stringify(['workspace-last-sync', options.userId, workspaceId]))
    return value === null ? null : Number(value)
  }
  /** Status writes in flight; waitForSync joins them so its caller reads the round's final status. */
  const statusWrites = new Set<Promise<void>>()
  function publishSyncStatus(workspaceId: string, update: Partial<WorkspaceSyncStatus>) {
    const write = writeSyncStatus(workspaceId, update)
    statusWrites.add(write)
    const settled = () => { statusWrites.delete(write) }
    void write.then(settled, settled)
    return write
  }
  function writeSyncStatus(workspaceId: string, update: Partial<WorkspaceSyncStatus>) {
    if (lifetime.signal.aborted) return Promise.resolve()
    syncStatuses.set(workspaceId, { offline: false, lastSyncedAt: lastSync(workspaceId), error: null, ...syncStatuses.get(workspaceId), ...update })
    if (update.lastSyncedAt !== null && update.lastSyncedAt !== undefined) options.storage.setItem(JSON.stringify(['workspace-last-sync', options.userId, workspaceId]), String(update.lastSyncedAt))
    // Status is already known; publishing it needs no cache invalidation or read.
    options.directoryClient.setQueryData(syncStatusOptions(workspaceId).queryKey, syncStatuses.get(workspaceId))
    return Promise.resolve()
  }
  function reportSyncFailure(workspaceId: string, error: unknown) {
    void publishSyncStatus(workspaceId, { offline: isReplicationOffline(error), error }).catch(() => {})
  }
  const reexpressing = new Map<string, Promise<void>>()
  /** db-crdt-sync spec :37: work saved in a replaced file, held until the next open's file holds it; then the old file is deleted. */
  const carried = new Map<string, { rows: Record<string, unknown>[] }>()
  async function carryInto(workspaceId: string, file: SpaceFile) {
    const work = carried.get(workspaceId)
    if (!work) return
    await importPendingWork(pendingStore(safe(file.db.driver)), work.rows)
    carried.delete(workspaceId)
  }
  /** db-crdt-sync spec :37: once the snapshot download is complete, re-express work persisted before a reset, once. */
  function reexpressPersisted(workspaceId: string, file: SpaceFile): Promise<void> {
    const running = reexpressing.get(workspaceId)
    if (running) return running
    const task = (async () => {
      if (readResetImports(options.storage, options.userId).includes(workspaceId)) return
      const driver = file.db.driver as TursoWorkerDatabaseDriver
      const [held] = await driver.query<{ n: number }>("SELECT count(*) AS n FROM sqlite_schema WHERE type='table' AND name='_turso_reset_pending'")
      if (!held?.n || !(await readReplicaState(driver)).initialReplayComplete) return
      // The directory read after the reset names the new personal root (db-crdt-sync.spec.md :uc[root-pages-have-ulid-ids]).
      const personalRootId = safe((directories.get(workspaceId) ?? [])[0]).personalRootId
      const { applied, refused } = await reexpressPendingWork(file.db, pendingStore(driver), { userId: options.userId, spaceId: workspaceId, personalRootId, now: new Date(), tel: options.tel })
      for (const { row, error } of refused) options.tel.warn('Pending write refused after replica reset', { workspaceId, table: row.table, error: String(error) })
      if (applied.length > 0 || refused.length > 0) void afterLocalCommit(workspaceId).catch(error => { options.tel.error(error, { operation: 'workspace.reexpress', workspaceId }) })
    })().finally(() => { reexpressing.delete(workspaceId) })
    reexpressing.set(workspaceId, task)
    return task
  }
  function open(workspaceId: string, tel: Tel): Promise<boolean> {
    lifetime.signal.throwIfAborted()
    if (failures.get(workspaceId) instanceof ReplicationInvariantError) return Promise.reject(unknownErrorToError(failures.get(workspaceId)))
    // Opens join this task; the file becomes visible only after journal replay.
    const existing = opening.get(workspaceId)
    if (existing) return existing
    if (files.has(workspaceId)) return Promise.resolve(false)
    const task = (async () => {
      const spaces = directories.get(workspaceId) ?? []
      const space = spaces.find(space => space.kind === 'private' && space.ownerUserId === options.userId) ?? spaces[0]
      if (!space) throw new Error('Workspace unavailable')
      const file = await options.open(space, tel, lifetime.signal)
      await carryInto(workspaceId, file)
      const nativeDriver = safe(file.db.driver)
      const closeUnobserved = file.close.bind(file)
      file.close = async () => {
        const observation = outboxes.get(nativeDriver)
        outboxes.delete(nativeDriver)
        if (observation) (await observation).close()
        await closeUnobserved()
      }
      // The opener may have held a production reset's data (spaces-browser adoptRetiredReplicas): show the import screen.
      if (readResetImports(options.storage, options.userId).includes(workspaceId)) void publishResetImports()
      // A refused read (retired or resetting file) never succeeds on this client: no retry delay,
      // so mounted readers settle at once and render unavailable instead of holding a suspended batch.
      const queryClient = new QueryClient({ queries: { retry: (failures, error) => !(error instanceof WorkspaceClosedError) && failures <= 3 } })
      try {
        lifetime.signal.throwIfAborted()
        const replication = await openWorkspaceReplication({ db: file.db, userId: options.userId, spaceId: workspaceId, tel: options.tel, start: false, ownership: file.ownership,
          afterExchange: async () => {
            // Engine recovery writes dead letters during the exchange. Join
            // their publication even when no SQL subscription notified the UI.
            const problems = queryClient.getQuery(['sync-problems', file.path])
            if (problems) await problems.fetchFresh()
            await reexpressPersisted(workspaceId, file)
            await pokeAfterPush(workspaceId, nativeDriver)
          },
          onOwner: () => { void trigger(workspaceId, true, options.tel, true).catch(() => {}) },
          onPending: pending => {
            void publishSyncStatus(workspaceId, { pending }).catch(() => {})
            // Native rounds the scheduler runs on its own push too; their end announces the push like an explicit round.
            if (!pending) void pokeAfterPush(workspaceId, nativeDriver)
          },
          onApplied: () => {
            if (!opening.has(workspaceId)) publishPermissions()
            file.translations?.update()
            void publishSyncStatus(workspaceId, { lastSyncedAt: Date.now(), offline: false, error: null }).catch(() => {})
          },
          onHalt: halt => { void publishSyncStatus(workspaceId, { halted: halt }).catch(() => {}) },
          onError: error => {
            failures.set(workspaceId, error)
            reportSyncFailure(workspaceId, error)
            if (error instanceof ReplicaTransportError && isWorkspaceAccessRejection(error)) {
              requestReplacement(workspaceId, error)
            }
          },
        })
        // Mounted readers belong to this opening, even when reopening retains
        // the native replica identity. Never reuse readers from a closed file.
        const replicaId = Array.from(file.replicaIdentity, byte => byte.toString(16).padStart(2, '0')).join('')
        const db = file.db.withTel(options.tel)
        const gate = createReaderGate(safe(db.driver))
        db.setDriver(gate.driver)
        const driver = createCustomDriver(gate.driver, {
          appTables,
          defaultSpaceId: space.personalRootId,
          recovery: { cdbPreCommit },
        })
        const commit = <T>(run: () => Promise<T>) => writeWorkspace({ writes, afterCommit: () => afterFileCommit(workspaceId), afterLocalCommit: () => afterFileCommit(workspaceId) }, workspaceId, run)
        const readOnly = (sql: string) => { const kind = classify(sql, { appTables }); return !('code' in kind) && kind.type === 'read' }
        const refuseRawCreate = (sql: string) => {
          for (const statement of splitStatements(sql)) {
            const kind = classify(statement, { appTables })
            if (!('code' in kind) && kind.type === 'table' && kind.action === 'create') throw Object.assign(new Error('E_RAW_CREATE: create a database from the app'), { code: 'E_RAW_CREATE' })
          }
        }
        const guardSqlDriver = (scoped: CustomDriver): CustomDriver => {
          return { ...scoped,
            exec: (sql, params, context) => { refuseRawCreate(sql); return scoped.exec(sql, params, context) },
            query: <T>(sql: string, params?: unknown[], context?: Parameters<CustomDriver['query']>[2]) => { refuseRawCreate(sql); return scoped.query<T>(sql, params, context) },
            run: (statements, meta) => { for (const statement of statements) refuseRawCreate(statement.sql); return scoped.run(statements, meta) },
            transaction: (fn, context) => scoped.transaction(tx => fn(guardSqlDriver(tx)), context),
          }
        }
        const guarded = guardSqlDriver(driver)
        const customDb: CustomDriver = {
          ...driver,
          close: undefined, // The workspace file owns and closes the underlying connection.
          createDatabase: input => commit(() => driver.createDatabase(input)),
          createDatabaseRow: input => commit(() => driver.createDatabaseRow(input)),
          query: <T>(sql: string, params?: unknown[], ctx?: Parameters<CustomDriver['exec']>[2]) => readOnly(sql) ? guarded.query<T>(sql, params, ctx) : commit(() => guarded.query<T>(sql, params, ctx)),
          exec: (sql, params, ctx) => readOnly(sql) ? guarded.exec(sql, params, ctx) : commit(() => guarded.exec(sql, params, ctx)),
          run: (statements, meta) => commit(() => guarded.run(statements, meta)),
          transaction: (fn, ctx) => commit(() => guarded.transaction(fn, ctx)),
          reapply: (program, complete) => commit(() => driver.reapply(program, complete)),
          copyFrom: (...args) => commit(() => driver.copyFrom(...args)),
        }
        const deadLetters = createDeadLetterService({ driver: gate.driver, customDriver: customDb })
        const opened = { ...file, db, customDb, deadLetters, databaseRenames: createDatabaseRenames(queryClient), replicaId, connectionId: ulid(), queryClient, replication }
        readerGates.set(opened, gate)
        // A page unloaded before its burst committed left it in the unload journal: commit it before readers open.
        await replayPageUnloads({ storage: options.storage, userId: options.userId, workspaceId, tel: options.tel,
          commit: (pageId, frame) => commit(() => commitPageEdits(db, [{ pageId, userId: options.userId, now: new Date(), tel: options.tel, frame, bucket: options.bucket?.() }])) })
        await publishSyncStatus(workspaceId, { lastSyncedAt: Date.now(), offline: false, error: null })
        let search: ReturnType<typeof startPageSearchRefresh> | undefined
        let closed = false
        const closeFile = opened.close.bind(opened)
        opened.close = async () => { closed = true; await search?.close(); await closeFile() }
        files.set(workspaceId, opened)
        options.realtime?.opened(workspaceId)
        failures.delete(workspaceId)
        await publish(workspaceId)
        // Publish the final resource before maintenance starts; never start it after a published reader closed the file.
        if (!closed) await consumeAppliedPageKeys(nativeDriver)
        if (!closed) {
          search = startPageSearchRefresh(db, error => { options.tel.error(error, { operation: 'page.search.refresh', workspaceId }) })
          file.embeddings?.update()
        }
        return true
      } catch (error) { queryClient.destroy(); await file.close(); throw error }
    })().catch(error => {
      if (isDeletedWorkspaceReset(error)) {
        directories.delete(workspaceId)
        options.storage.removeItem(JSON.stringify(['space-directory', options.userId, workspaceId]))
      }
      failures.set(workspaceId, error); throw error
    }).finally(() => {
      opening.delete(workspaceId)
      // A retained file is readable before its refresh finishes; fresh files already exchanged during initial replay.
      if (!failures.has(workspaceId) && files.get(workspaceId)?.syncedOnOpen === false) refreshOpened(workspaceId, tel)
    })
    opening.set(workspaceId, task)
    return task
  }
  function refreshOpened(workspaceId: string, tel: Tel) {
    void trigger(workspaceId, false, tel).catch(error => {
      if (!lifetime.signal.aborted) reportSyncFailure(workspaceId, error)
    })
  }
  async function refresh(workspaceId: string, tel: Tel): Promise<void> { await refreshSeeding(workspaceId, tel) }
  async function openAvailable(workspaceId: string, tel: Tel) {
    try { const opened = await open(workspaceId, tel); return opened }
    catch (error) {
      // Keep the shell and directory available while a first download is blocked offline.
      if (!isReplicationOffline(error)) throw error
      await publishSyncStatus(workspaceId, { offline: true, error })
      return false
    }
  }
  /** Resolves true when the directory read succeeded: `list` seeded the levels query with its result. */
  function refreshSeeding(workspaceId: string, tel: Tel): Promise<boolean> {
    const task = refreshDirectory(workspaceId, tel)
    acquiring.add(task)
    return task.finally(() => { acquiring.delete(task) })
  }
  async function refreshDirectory(workspaceId: string, tel: Tel): Promise<boolean> {
    lifetime.signal.throwIfAborted()
    const key = JSON.stringify(['space-directory', options.userId, workspaceId])
    const cached = directories.get(workspaceId) ?? JSON.parse(options.storage.getItem(key) ?? '[]') as Space[]
    if (cached.length > 0 && !files.has(workspaceId) && !replacing.has(workspaceId)) {
      directories.set(workspaceId, cached)
      if (!await openAvailable(workspaceId, tel)) await publish(workspaceId)
      return false
    }
    let spaces: Space[]
    let seeded = false
    try { spaces = await options.list(workspaceId, tel, lifetime.signal); seeded = true }
    catch (error) {
      if (error instanceof RpcError && error.status === 403) {
        await publishSyncStatus(workspaceId, { offline: false, error })
        if (cached.length === 0) throw error
      }
      if (cached.length === 0) throw error
      spaces = cached
    }
    lifetime.signal.throwIfAborted()
    directories.set(workspaceId, spaces)
    options.storage.setItem(key, JSON.stringify(spaces))
    let opened = false
    try { opened = await openAvailable(workspaceId, tel) }
    finally {
      if (!opened) await publish(workspaceId)
      // A successful read already seeded the levels query (`list`); a failed one refetches it so its refusal reaches readers.
      if (!seeded) publishPermissions()
    }
    return seeded
  }
  /** `listed` means the foreground caller already refreshed workspaces; other directory demands refresh roles/membership too. */
  function trigger(workspaceId: string, refreshDirectory: boolean, tel: Tel, listed = false): Promise<void> {
    // Each demand reaches native even while an earlier receipt is pending.
    // Serializing here makes an offline burst wait through one backoff per save.
    const tasks = triggering.get(workspaceId) ?? new Set<Promise<void>>()
    triggering.set(workspaceId, tasks)
    const task = triggerWorkspace(workspaceId, refreshDirectory, tel, listed)
    tasks.add(task)
    return task.finally(() => {
      tasks.delete(task)
      if (tasks.size === 0) triggering.delete(workspaceId)
    })
  }
  async function triggerWorkspace(workspaceId: string, refreshDirectory: boolean, tel: Tel, listed: boolean) {
    // A 410 may start replacement through the native subscription before this demand.
    // M8: explicit sync must wait for reset and re-download, not an aborted handle.
    await replacing.get(workspaceId)
    const alreadyOpen = files.has(workspaceId)
    if (refreshDirectory) {
      // An explicit refresh re-reads roles/membership the server owns (another admin may have changed them).
      // Opening and the focus refresh list workspaces themselves; any other explicit refresh invalidates.
      if (!listed) {
        const queries = await options.directoryClient.invalidateQueries({ queryKey: ['workspace'] })
        await Promise.all(queries.filter(query => query.state.isInvalidated).map(query => query.fetch('workspace.directory.refresh')))
      }
      try { await refreshSeeding(workspaceId, tel) }
      catch (error) { await publishSyncStatus(workspaceId, { offline: isReplicationOffline(error), error }); throw error }
    }
    if (!directories.has(workspaceId) || options.remote === false) return
    const file = files.get(workspaceId)
    if (!file) throw unknownErrorToError(failures.get(workspaceId) ?? new Error('First download needed'))
    try {
      // Opening already performed startup contact. Only a retained file needs a new exchange.
      if (!alreadyOpen && file.syncedOnOpen !== false) { await file.replication.bootstrap(); return }
      // db-crdt-sync spec :37: the engine dead-letters a received freeze snapshot it cannot adopt because this device
      // holds ops the snapshot lacks and the freeze map that translated them is gone (fork f1044a49d, reason
      // RESET_REQUIRED). The device missed the window: it resets from the snapshot and re-expresses its pending work,
      // as for a push refused below the floor (410 eligibility_expired). Checked after a failed round too: the pull
      // that dead-letters it can precede a push that fails offline.
      const result = await file.replication.sync().catch(async (error: unknown) => {
        if (await resetRequired(file)) return resetMarker
        throw error
      })
      if (result === resetMarker || await resetRequired(file)) {
        await replace(workspaceId, new ReplicaTransportError(410, 'eligibility_expired', 'A received freeze snapshot requires a reset (RESET_REQUIRED)', null), tel)
        return
      }
      if (result) {
        await file.replication.bootstrap()
        await publishSyncStatus(workspaceId, { lastSyncedAt: Date.now(), offline: false, error: null })
      }
    }
    catch (error) {
      // Closing/replacing this file cancels its outstanding demands. The new
      // workspace resource owns status; retirement is not a sync failure.
      if (files.get(workspaceId) !== file && error instanceof Error && error.name === 'AbortError') return
      if (error instanceof ReplicationInvariantError) {
        failures.set(workspaceId, error)
        await closeWorkspace(workspaceId).catch(cleanupError => { options.tel.error(cleanupError, { operation: 'workspace.release', workspaceId }) })
        throw error
      }
      if (error instanceof ReplicaTransportError && isWorkspaceAccessRejection(error)) { await replace(workspaceId, error, tel); return }
      await publishSyncStatus(workspaceId, { offline: isReplicationOffline(error), error })
      if (isReplicationOffline(error) || isReplicationOutcome(error) || error instanceof ReplicaTransportError) return
      throw error
    }
  }
  function get(locator: string): SpaceResource {
    lifetime.signal.throwIfAborted()
    const workspaceId = directories.has(locator) ? locator : [...directories.entries()].find(([, spaces]) => spaces.some(space => space.id === locator))?.[0]
    if (!workspaceId) throw new Error(`Workspace ${locator} is not open`)
    const view = snapshot(workspaceId)
    const resource = view.resources.find(resource => resource.space.id === locator) ?? view.resources.find(resource => resource.space.kind === 'private' && resource.space.ownerUserId === options.userId) ?? view.resources[0]
    // No resource and no recorded failure: the file was closed (retired), a typed refusal routes render as unavailable.
    if (!resource) throw unknownErrorToError(failures.get(workspaceId) ?? new WorkspaceClosedError())
    // The session layer names this realm's CRDT author (crdt facade.spec "Author"): every device access acts as the
    // device's user, so a realm hosting several devices (product tests) stamps each edit with its device's user.
    actAs(options.userId)
    return resource
  }
  function afterLocalCommit(locator: string): Promise<void> {
    // Replica notifications wake embedding maintenance; local settlement adds no demand.
    const resource = get(locator)
    return afterFileCommit(resource.space.workspaceId)
  }
  function afterFileCommit(workspaceId: string): Promise<void> {
    // The native scheduler pushes this commit; the end of that round pokes the peers (pokeAfterPush).
    unpoked.add(workspaceId)
    files.get(workspaceId)?.translations?.update()
    return Promise.resolve()
  }
  async function closeWorkspace(workspaceId: string) {
    await opening.get(workspaceId)?.catch(() => {})
    const file = files.get(workspaceId)
    files.delete(workspaceId)
    options.realtime?.closed(workspaceId)
    if (file) {
      // Withdraw the published resource before clearing readers or closing its
      // worker. Mounted views must not restart queries on the retiring file.
      await publish(workspaceId)
      // Directory publication has settled. Every retiring connection loses its
      // readers, including an ordinary close/reopen; its replacement owns a new cache.
      file.queryClient.destroy()
      file.replication.stop(); file.translations?.close(); file.embeddings?.close()
      await disposeAll([() => file.replication.close(), () => closeReaders(file, workspaceId), () => file.embeddings?.waitForIdle(),
        () => { gateOf(file).close(); return file.close() }, () => publish(workspaceId)], 'Workspace shutdown failed')
    }
    await publish(workspaceId)
  }
  /** Resolves once the in-flight reads and the page readers released the file; every release is joined before the first failure is reported. `refuse` also rejects later reads (the file is being dropped for good). */
  async function closeReaders(file: WorkspaceFile, workspaceId: string, refuse = false) {
    const gate = gateOf(file)
    if (refuse) gate.refuse()
    const results = await Promise.allSettled([...(directories.get(workspaceId) ?? []).map(async space => { await options.closeReaders?.(space.id) }), gate.drained()])
    const failed = results.find(result => result.status === 'rejected')
    if (failed) throw failed.reason as unknown
  }
  function requestReplacement(workspaceId: string, error: ReplicaTransportError) {
    void replace(workspaceId, error, options.tel).catch(failure => { failures.set(workspaceId, failure) })
  }
  function replace(workspaceId: string, error: ReplicaTransportError, tel: Tel): Promise<void> {
    const current = replacing.get(workspaceId)
    if (current) return current
    const task = (async () => {
      lifetime.signal.throwIfAborted()
      await opening.get(workspaceId)?.catch(() => {})
      const file = files.get(workspaceId)
      syncStatuses.delete(workspaceId)
      const deleted = isDeletedWorkspaceReset(error)
      let resetDownloadFailure: { error: unknown } | undefined
      failures.set(workspaceId, error)
      await publish(workspaceId)
      // db-crdt-sync spec :37: a replica refused for a missed freeze window keeps its pending work. It is persisted
      // in the replica's technical table before the reset starts and re-expressed after the next exchange that
      // completes the snapshot download (reexpressPersisted), even across a failed reset or an app restart. It is
      // persisted after replication closes: an exchange still finishing on the old replica would re-express it there.
      if (file) {
        file.replication.stop()
        file.translations?.close(); file.embeddings?.close()
        await disposeAll([() => file.replication.close(), () => closeReaders(file, workspaceId, true),
          () => file.translations?.waitForIdle(), () => file.embeddings?.waitForIdle()], 'Workspace readers shutdown failed')
        // Cleared before the capture writes: its writes would otherwise refetch the closed readers' queries.
        file.queryClient.clear()
        const production = !deleted && error.code === 'production_reset'
        if (!deleted && (error.code === 'eligibility_expired' || production)) {
          const store = pendingStore(gateOf(file).original)
          // requirements.md 2026-10-27 16:5x: after a production reset the device's data waits for the user's import.
          if (production) { holdResetImport(options.storage, options.userId, workspaceId); await publishResetImports() }
          const captured = (production ? await persistLocalWork(store) : 0) + await persistPendingWork(store)
          if (production && captured === 0) { releaseResetImport(options.storage, options.userId, workspaceId); await publishResetImports() }
          // A replacement file (browser retirement, a fresh in-memory file) lacks this table: carry the rows into it.
          const rows = await exportPendingWork(store)
          if (rows.length > 0) carried.set(workspaceId, { rows })
        }
        {
          await using retirement = new AsyncDisposableStack()
          retirement.defer(() => closeWorkspace(workspaceId))
          const previousIdentity = new Uint8Array(file.replicaIdentity)
          if (!deleted && file.retireReplica) {
            await file.retireReplica()
          } else if (!deleted) {
            try { await resetWhenIdle(file) }
            catch (resetError) {
              // Reset commits its fresh identity before downloading. A failed
              // download still requires retirement and an ordinary fresh open.
              let state: TursoReplicaState
              try { state = await readReplicaState(gateOf(file).original as TursoWorkerDatabaseDriver) }
              catch (stateError) { throw new SuppressedError(stateError, resetError, 'Failed to inspect replica after reset failed') }
              if (state.initialReplayComplete || state.identity.every((byte, index) => byte === previousIdentity[index])) throw resetError
              resetDownloadFailure = { error: resetError }
            }
          }
        }
        if (deleted) await file.purge()
      }
      if (deleted) {
        directories.delete(workspaceId)
        options.storage.removeItem(JSON.stringify(['space-directory', options.userId, workspaceId]))
        await publish(workspaceId)
        return
      }
      try {
        await refresh(workspaceId, tel)
        const reopened = files.get(workspaceId)
        if (reopened) await reexpressPersisted(workspaceId, reopened)
      }
      catch (openError) {
        if (resetDownloadFailure) throw new SuppressedError(openError, resetDownloadFailure.error, 'Failed to reopen committed replica reset')
        throw openError
      }
    })().finally(() => { replacing.delete(workspaceId); void publish(workspaceId).catch(error => { options.tel.error(error, { operation: 'workspace.publish', workspaceId }) }) })
    replacing.set(workspaceId, task)
    return task
  }
  /** requirements.md 2026-10-27 16:5x: workspaces whose pre-reset data waits for the user's "Import your data". */
  function resetImportOptions() {
    return { queryKey: ['workspace-reset-import', options.userId], staleTime: Infinity, queryFn: () => Promise.resolve(readResetImports(options.storage, options.userId)) }
  }
  function publishResetImports() { return options.directoryClient.invalidateQueries({ queryKey: resetImportOptions().queryKey }) }
  /** The user's "Import your data": the held pre-reset data is re-expressed into the workspace (db-crdt-sync spec re-express), then synced. */
  async function importResetData(workspaceId: string, tel: Tel) {
    // The hold is released only once the reset settled: a replace still in flight would otherwise re-express inside
    // itself, while its file is not yet published (get() refuses it).
    await replacing.get(workspaceId)
    releaseResetImport(options.storage, options.userId, workspaceId)
    await open(workspaceId, tel)
    await reexpressPersisted(workspaceId, safe(files.get(workspaceId), 'INV: an opened workspace has a file'))
    await publishResetImports()
  }
  /** flashcards-local-first.spec.md Attach step 3: rows carried from another principal's file are held in this
   * workspace's file as work its server lacks, then re-expressed as writes by this user (the reset import path). Held
   * rows survive a restart: the next open re-expresses them once the snapshot download is complete. */
  async function importCarriedRows(workspaceId: string, rows: readonly PendingRow[], tel: Tel, held: () => void) {
    await open(workspaceId, tel)
    const file = safe(files.get(workspaceId), 'INV: an opened workspace has a file')
    await persistRows(pendingStore(safe(file.db.driver)), replicatedTables, rows)
    held()
    await reexpressPersisted(workspaceId, file)
  }
  /** R9-2: the engine rejects a reset while a round is in flight ("replica busy"); await that round's end, then reset again. */
  function resetWhenIdle(file: WorkspaceFile) {
    // INV-P2 / access-reset-never-publishes-unsent-note-or-placement:
    // reset discards pending work. Starting another exchange could publish it.
    const driver = gateOf(file).original as TursoWorkerDatabaseDriver
    if (!file.resetReplica) throw new Error('Native replica reset required')
    const reset = file.resetReplica.bind(file)
    return resetSpaceReplicaWhenIdle(driver, reset)
  }
  function cancel() { lifetime.abort(new DOMException('Workspace resources closed', 'AbortError')) }
  return {
    userId: options.userId,
    queryOptions, syncStatusOptions, resetImportOptions, importResetData, importCarriedRows, snapshot, refresh, trigger, get, afterLocalCommit, cancel, closeWorkspace,
    /** Join work already scheduled by a local commit; never create another demand. */
    async waitForSync(locator: string): Promise<void> {
      if (options.remote === false) return
      const workspaceId = directories.has(locator) ? locator : get(locator).space.workspaceId
      const tasks = triggering.get(workspaceId)
      if (tasks) await Promise.all(tasks)
      const file = files.get(workspaceId)
      if (!file) return
      const lastError = syncStatuses.get(workspaceId)?.error
      if (isReplicationOffline(lastError)) throw unknownErrorToError(lastError)
      const driver = gateOf(file).original as TursoWorkerDatabaseDriver & TransportStatusDriver
      const outbox = await observeOutbox(driver)
      let state: TursoReplicaState | undefined
      let transportError: unknown = lastError
      let changed = Promise.withResolvers<void>()
      let revision = 0
      const wake = () => { revision += 1; changed.resolve() }
      const releaseNative = await driver.subscribeReplica(value => { state = value; if (value.status !== 'idle') outbox.invalidate(); wake() })
      const releaseTransport = driver.subscribeTransport?.(error => { transportError = error; wake() })
      let releaseOutbox: (() => void) | undefined
      lifetime.signal.addEventListener('abort', wake)
      try {
        releaseOutbox = await driver.subscribe(outboxSql, wake)
        for (;;) {
          const pendingTasks = triggering.get(workspaceId)
          if (pendingTasks) await Promise.all(pendingTasks)
          changed = Promise.withResolvers<void>()
          lifetime.signal.throwIfAborted()
          if (files.get(workspaceId) !== file || state?.status === 'closed' || state?.status === 'closing') throw new WorkspaceClosedError()
          if (isReplicationOffline(transportError)) throw unknownErrorToError(transportError)
          if (state?.status === 'error' && (isReplicationOffline(state.error) || !isReplicationTransient(state.error))) throw unknownErrorToError(state.error)
          // INV-sync-join: observe a live outbox read and native idle; token Done alone is insufficient.
          const observedRevision = revision
          const pending = await outbox.read()
          if (pending === 0 && state?.status === 'idle' && !triggering.get(workspaceId)?.size) {
            // The idle round's onApplied publishes lastSyncedAt; return only after that final status write.
            await file.replication.applied()
            while (statusWrites.size > 0) await Promise.allSettled(statusWrites)
            // Finalization yields: a newly started native round invalidates the earlier idle/outbox observation.
            if (revision !== observedRevision || triggering.get(workspaceId)?.size) continue
            return
          }
          await changed.promise
        }
      } finally { releaseOutbox?.(); releaseTransport?.(); releaseNative(); lifetime.signal.removeEventListener('abort', wake) }
    },
    db: (locator: string) => get(locator).db,
    customDb: (locator: string) => get(locator).customDb,
    afterCommit(locator: string) {
      const resource = get(locator)
      // Local saves settle locally. The scheduler owns the retained native demand,
      // including transport retries; explicit Refresh joins it through trigger().
      unpoked.add(resource.space.workspaceId)
      resource.translations?.update()
      void trigger(resource.space.workspaceId, false, options.tel).catch(error => {
        if (!lifetime.signal.aborted) reportSyncFailure(resource.space.workspaceId, error)
      })
      return Promise.resolve()
    },
    async openWorkspace(workspaceId: string, tel: Tel) {
      lifetime.signal.throwIfAborted()
      if (opening.has(workspaceId)) { await open(workspaceId, tel); return }
      if (files.has(workspaceId)) { refreshOpened(workspaceId, tel); return }
      const cached = JSON.parse(options.storage.getItem(JSON.stringify(['space-directory', options.userId, workspaceId])) ?? '[]') as Space[]
      if (cached.length > 0) {
        directories.set(workspaceId, cached)
        try { await open(workspaceId, tel) }
        catch (error) {
          if (!isReplicationOffline(error)) throw error
          await publishSyncStatus(workspaceId, { offline: true, error })
          await publish(workspaceId)
        }
      } else await trigger(workspaceId, true, tel, true)
    },
    async refreshRetained(tel: Tel) {
      const retained: string[] = []
      for (let index = 0; index < options.storage.length; index++) {
        const key = options.storage.key(index)
        if (!key?.startsWith('["space-directory",')) continue
        const parts = JSON.parse(key) as unknown[]
        if (parts[1] === options.userId && typeof parts[2] === 'string' && !directories.has(parts[2])) retained.push(parts[2])
      }
      await Promise.allSettled(retained.map(async workspaceId => {
        const cached = JSON.parse(options.storage.getItem(JSON.stringify(['space-directory', options.userId, workspaceId])) ?? '[]') as Space[]
        if (cached.length === 0) return
        directories.set(workspaceId, cached)
        await open(workspaceId, tel)
        // Opening validates the retained admission or initial replay; one contact on startup.
      }))
    },
    async refreshOpen(tel: Tel) { await Promise.allSettled([...directories.keys()].map(workspaceId => trigger(workspaceId, true, tel, true))) },
    async revoke(spaceId: string) { const resource = get(spaceId); await refresh(resource.space.workspaceId, options.tel) },
    /** Sign-out: the retiring principal's readers stop before its user is removed; mounted views re-bind only in the commit that unmounts them. */
    clearReaders() { for (const file of files.values()) file.queryClient.clear() },
    async resetReplica(locator: string, tel: Tel) {
      const workspaceId = directories.has(locator) ? locator : get(locator).space.workspaceId
      await replace(workspaceId, new ReplicaTransportError(410, 'generation_reset'), tel)
    },
    close() {
      if (closing) return closing
      cancel()
      for (const file of files.values()) file.replication.stop()
      return closing = (async () => {
        await Promise.allSettled([...acquiring, ...opening.values(), ...replacing.values(), ...Array.from(triggering.values()).flatMap(tasks => Array.from(tasks))])
        await disposeAll([...files.keys()].map(workspaceId => () => closeWorkspace(workspaceId)), 'Workspace resources shutdown failed')
        directories.clear()
      })()
    },
  }
}
export type SpacesResources = ReturnType<typeof createSpacesResources>
